import React, { Suspense, lazy } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
} from "react-router-dom";
import { AppRoot } from "./AppRoot";
import { GuestRoute, ProtectedRoute } from "../components/ProtectedRoute";
import { RequireRole } from "../components/RequireRole";
import { DashboardPage } from "../pages/dashboard/DashboardPage";
import { LoginPage } from "../pages/LoginPage";
import { PaywallPage } from "../pages/PaywallPage";
import { ProtectedLayout } from "./ProtectedLayout";

// Simple loading fallback
const LazyFallback = () => (
  <div className="erp-page">
    <div className="erp-panel flex items-center justify-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-godo-accent"></div>
    </div>
  </div>
);

// Helper to handle named exports
const lazyNamed = <T extends Record<string, React.ComponentType<any>>, K extends keyof T>(
  loader: () => Promise<T>,
  name: K
) => lazy(() => loader().then((mod) => ({ default: mod[name] })));

// Lazy loaded components
const CrmDashboardPage = lazyNamed(() => import("../features/crm"), "CrmDashboardPage");
const GlDashboardPage = lazyNamed(() => import("../features/gl"), "GlDashboardPage");
const HrDashboardPage = lazyNamed(() => import("../features/hr"), "HrDashboardPage");
const InventoryDashboardPage = lazyNamed(() => import("../features/inventory"), "InventoryDashboardPage");
const PurchasesDashboardPage = lazyNamed(() => import("../features/purchases"), "PurchasesDashboardPage");
const PurchaseBillDetailPage = lazyNamed(() => import("../pages/purchases/PurchaseBillDetailPage"), "PurchaseBillDetailPage");
const SalesDashboardPage = lazyNamed(() => import("../features/sales"), "SalesDashboardPage");
const ForbiddenPage = lazyNamed(() => import("../pages/errors/ForbiddenPage"), "ForbiddenPage");
const FadvisorPage = lazyNamed(() => import("../pages/fadvisor/FadvisorPage"), "FadvisorPage");
const ReportsPage = lazyNamed(() => import("../pages/reports/ReportsPage"), "ReportsPage");
const AiAdvisorPage = lazyNamed(() => import("../pages/ai/AiAdvisorPage"), "AiAdvisorPage");
const ChartOfAccountsPage = lazyNamed(() => import("../pages/coa/ChartOfAccountsPage"), "ChartOfAccountsPage");
const AgingPage = lazyNamed(() => import("../pages/aging/AgingPage"), "AgingPage");
const InventoryValuationPage = lazyNamed(() => import("../features/inventory/pages/InventoryValuationPage"), "InventoryValuationPage");
const StockCountPage = lazyNamed(() => import("../features/inventory/pages/StockCountPage"), "StockCountPage");
const BankReconPage = lazyNamed(() => import("../pages/bank-recon/BankReconPage"), "BankReconPage");
const VatReturnPage = lazyNamed(() => import("../pages/vat-return/VatReturnPage"), "VatReturnPage");
const ZakatReportPage = lazyNamed(() => import("../pages/zakat/ZakatReportPage"), "ZakatReportPage");
const PartyStatementPage = lazyNamed(() => import("../pages/party-statement/PartyStatementPage"), "PartyStatementPage");
const JournalEntriesPage = lazyNamed(() => import("../pages/journal-entries/JournalEntriesPage"), "JournalEntriesPage");
const CashFlowPage = lazyNamed(() => import("../pages/cash-flow/CashFlowPage"), "CashFlowPage");
const AccountingPeriodsPage = lazyNamed(() => import("../pages/periods/AccountingPeriodsPage"), "AccountingPeriodsPage");
const ProfitabilityPage = lazyNamed(() => import("../pages/profitability/ProfitabilityPage"), "ProfitabilityPage");
const ZktecoPage = lazyNamed(() => import("../pages/zkteco/ZktecoPage"), "ZktecoPage");
const ModulePlaceholderPage = lazyNamed(() => import("../pages/ModulePlaceholderPage"), "ModulePlaceholderPage");
const PosPage = lazyNamed(() => import("../pages/pos/PosPage"), "PosPage");
const RestaurantCashierPage = lazyNamed(() => import("../pages/pos/RestaurantCashierPage"), "RestaurantCashierPage");
const ProductionBomDashboardPage = lazyNamed(() => import("../features/production/pages/ProductionBomDashboardPage"), "ProductionBomDashboardPage");
const ProductionHubPage = lazyNamed(() => import("../features/production/pages/ProductionHubPage"), "ProductionHubPage");
const ProductionRunFlowPage = lazyNamed(() => import("../features/production/pages/ProductionRunFlowPage"), "ProductionRunFlowPage");
const ProductionRunsDashboardPage = lazyNamed(() => import("../features/production/pages/ProductionRunsDashboardPage"), "ProductionRunsDashboardPage");
const RoutingFlowPage = lazyNamed(() => import("../features/production/pages/RoutingFlowPage"), "RoutingFlowPage");
const RoutingsDashboardPage = lazyNamed(() => import("../features/production/pages/RoutingsDashboardPage"), "RoutingsDashboardPage");
const ProductionWorkOrdersDashboardPage = lazyNamed(() => import("../features/production/pages/ProductionWorkOrdersDashboardPage"), "ProductionWorkOrdersDashboardPage");
const ProductionBomFormPage = lazyNamed(() => import("../pages/production/ProductionBomFormPage"), "ProductionBomFormPage");
const ProductionWorkOrderDetailPage = lazyNamed(() => import("../pages/production/ProductionWorkOrderDetailPage"), "ProductionWorkOrderDetailPage");
const DepreciationRunPage = lazyNamed(() => import("../pages/fixed-assets/DepreciationRunPage"), "DepreciationRunPage");
const FixedAssetFormPage = lazyNamed(() => import("../pages/fixed-assets/FixedAssetFormPage"), "FixedAssetFormPage");
const FixedAssetsPage = lazyNamed(() => import("../pages/fixed-assets/FixedAssetsPage"), "FixedAssetsPage");
const SalesInvoiceDetailPage = lazyNamed(() => import("../pages/sales/SalesInvoiceDetailPage"), "SalesInvoiceDetailPage");
const EtaSettingsPage = lazyNamed(() => import("../pages/settings/EtaSettingsPage"), "EtaSettingsPage");
const GlobalSettingsForm = lazyNamed(() => import("../pages/settings/GlobalSettingsForm"), "GlobalSettingsForm");
const SettingsLayout = lazyNamed(() => import("../pages/settings/SettingsLayout"), "SettingsLayout");
const ZatcaSettingsPage = lazyNamed(() => import("../pages/settings/ZatcaSettingsPage"), "ZatcaSettingsPage");
const ShopifyIntegrationPage = lazyNamed(() => import("../pages/settings/shopify/ShopifyIntegrationPage"), "ShopifyIntegrationPage");
const StoreIntegrationsHubPage = lazyNamed(() => import("../pages/settings/stores/StoreIntegrationsHubPage"), "StoreIntegrationsHubPage");
const AttendancePage = lazyNamed(() => import("../pages/hr/attendance/AttendancePage"), "AttendancePage");
const PayrollPage = React.lazy(() => import("../pages/payroll/PayrollPage"));
const PayrollSettingsPage = React.lazy(() => import("../pages/payroll/PayrollSettingsPage"));
const LeaveManagementPage = React.lazy(() => import("../pages/payroll/LeaveManagementPage"));
const ShippingCodPage = lazyNamed(() => import("../pages/shipping/ShippingCodPage"), "ShippingCodPage");
const DataMigrationPage = lazyNamed(() => import("../pages/DataMigrationPage"), "default");
const DirectoriesPage = lazyNamed(() => import("../pages/directories/DirectoriesPage"), "default");

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AppRoot />}>
      <Route path="/pay" element={<PaywallPage />} />

      <Route element={<GuestRoute />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route element={<ProtectedLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/forbidden" element={<Suspense fallback={<LazyFallback />}><ForbiddenPage /></Suspense>} />

          <Route
            path="/gl"
            element={
              <RequireRole moduleId="gl">
                <Suspense fallback={<LazyFallback />}><GlDashboardPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/coa"
            element={
              <RequireRole moduleId="coa">
                <Suspense fallback={<LazyFallback />}><ChartOfAccountsPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/aging"
            element={
              <RequireRole moduleId="aging">
                <Suspense fallback={<LazyFallback />}><AgingPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/inventory-valuation"
            element={
              <RequireRole moduleId="inventory_valuation">
                <Suspense fallback={<LazyFallback />}><InventoryValuationPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/stock-count"
            element={
              <RequireRole moduleId="stock_count">
                <Suspense fallback={<LazyFallback />}><StockCountPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/bank-recon"
            element={
              <RequireRole moduleId="bank_recon">
                <Suspense fallback={<LazyFallback />}><BankReconPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/vat-return"
            element={
              <RequireRole moduleId="vat_return">
                <Suspense fallback={<LazyFallback />}><VatReturnPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/zakat"
            element={
              <RequireRole moduleId="zakat">
                <Suspense fallback={<LazyFallback />}><ZakatReportPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/party-statement"
            element={
              <RequireRole moduleId="party_statement">
                <Suspense fallback={<LazyFallback />}><PartyStatementPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/journal-entries"
            element={
              <RequireRole moduleId="journal_entries">
                <Suspense fallback={<LazyFallback />}><JournalEntriesPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/cash-flow"
            element={
              <RequireRole moduleId="cash_flow">
                <Suspense fallback={<LazyFallback />}><CashFlowPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/accounting-periods"
            element={
              <RequireRole moduleId="accounting_periods">
                <Suspense fallback={<LazyFallback />}><AccountingPeriodsPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/profitability"
            element={
              <RequireRole moduleId="profitability">
                <Suspense fallback={<LazyFallback />}><ProfitabilityPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/zkteco"
            element={
              <RequireRole moduleId="zkteco">
                <Suspense fallback={<LazyFallback />}><ZktecoPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireRole moduleId="settings">
                <Suspense fallback={<LazyFallback />}><SettingsLayout /></Suspense>
              </RequireRole>
            }
          >
            <Route index element={<Suspense fallback={<LazyFallback />}><GlobalSettingsForm /></Suspense>} />
            <Route path="einvoice" element={<Suspense fallback={<LazyFallback />}><EtaSettingsPage /></Suspense>} />
            <Route path="zatca" element={<Suspense fallback={<LazyFallback />}><ZatcaSettingsPage /></Suspense>} />
            <Route path="stores" element={<Suspense fallback={<LazyFallback />}><StoreIntegrationsHubPage /></Suspense>} />
            <Route path="stores/shopify" element={<Suspense fallback={<LazyFallback />}><ShopifyIntegrationPage /></Suspense>} />
            <Route
              path="shopify"
              element={<Navigate to="/settings/stores/shopify" replace />}
            />
          </Route>
          <Route
            path="/purchases"
            element={
              <RequireRole moduleId="purchases">
                <Suspense fallback={<LazyFallback />}><PurchasesDashboardPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/purchases/bills/:billId"
            element={
              <RequireRole moduleId="purchases">
                <Suspense fallback={<LazyFallback />}><PurchaseBillDetailPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/fixed-assets"
            element={
              <RequireRole moduleId="fixed_assets">
                <Suspense fallback={<LazyFallback />}><FixedAssetsPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/fixed-assets/new"
            element={
              <RequireRole moduleId="fixed_assets">
                <Suspense fallback={<LazyFallback />}><FixedAssetFormPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/fixed-assets/depreciation-run"
            element={
              <RequireRole moduleId="fixed_assets">
                <Suspense fallback={<LazyFallback />}><DepreciationRunPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/fixed-assets/:assetId/edit"
            element={
              <RequireRole moduleId="fixed_assets">
                <Suspense fallback={<LazyFallback />}><FixedAssetFormPage /></Suspense>
              </RequireRole>
            }
          />

          <Route
            path="/hr"
            element={
              <RequireRole moduleId="hr">
                <Suspense fallback={<LazyFallback />}><HrDashboardPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/hr/attendance"
            element={
              <RequireRole moduleId="attendance">
                <Suspense fallback={<LazyFallback />}><AttendancePage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/payroll"
            element={
              <RequireRole moduleId="payroll">
                <Suspense fallback={<LazyFallback />}><PayrollPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/payroll/settings"
            element={
              <RequireRole moduleId="payroll_settings">
                <Suspense fallback={<LazyFallback />}><PayrollSettingsPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/payroll/leaves"
            element={
              <RequireRole moduleId="payroll">
                <Suspense fallback={<LazyFallback />}><LeaveManagementPage /></Suspense>
              </RequireRole>
            }
          />

          <Route
            path="/inventory"
            element={
              <RequireRole moduleId="inventory">
                <Suspense fallback={<LazyFallback />}><InventoryDashboardPage /></Suspense>
              </RequireRole>
            }
          />

          <Route
            path="/sales"
            element={
              <RequireRole moduleId="sales">
                <Suspense fallback={<LazyFallback />}><SalesDashboardPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/sales/invoices/:invoiceId"
            element={
              <RequireRole moduleId="sales">
                <Suspense fallback={<LazyFallback />}><SalesInvoiceDetailPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/pos"
            element={
              <RequireRole moduleId="pos">
                <Suspense fallback={<LazyFallback />}><PosPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/pos/cashier"
            element={
              <RequireRole moduleId="pos">
                <Suspense fallback={<LazyFallback />}><RestaurantCashierPage /></Suspense>
              </RequireRole>
            }
          />

          <Route
            path="/crm"
            element={
              <RequireRole moduleId="crm">
                <Suspense fallback={<LazyFallback />}><CrmDashboardPage /></Suspense>
              </RequireRole>
            }
          />

          <Route
            path="/shipping"
            element={
              <RequireRole moduleId="shipping">
                <Suspense fallback={<LazyFallback />}><ShippingCodPage /></Suspense>
              </RequireRole>
            }
          />

          <Route
            path="/production"
            element={
              <RequireRole moduleId="production">
                <Suspense fallback={<LazyFallback />}><ProductionHubPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/production/bom"
            element={
              <RequireRole moduleId="production">
                <Suspense fallback={<LazyFallback />}><ProductionBomDashboardPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/production/bom/new"
            element={<Navigate to="/production/bom" replace />}
          />
          <Route
            path="/production/bom/:bomId/edit"
            element={
              <RequireRole moduleId="production">
                <Suspense fallback={<LazyFallback />}><ProductionBomFormPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/production/work-orders"
            element={
              <RequireRole moduleId="production">
                <Suspense fallback={<LazyFallback />}><ProductionWorkOrdersDashboardPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/production/work-orders/new"
            element={<Navigate to="/production/work-orders" replace />}
          />
          <Route
            path="/production/work-orders/:workOrderId"
            element={
              <RequireRole moduleId="production">
                <Suspense fallback={<LazyFallback />}><ProductionWorkOrderDetailPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/production/routings"
            element={
              <RequireRole moduleId="production">
                <Suspense fallback={<LazyFallback />}><RoutingsDashboardPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/production/routings/:routingId/flow"
            element={
              <RequireRole moduleId="production">
                <Suspense fallback={<LazyFallback />}><RoutingFlowPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/production/runs"
            element={
              <RequireRole moduleId="production">
                <Suspense fallback={<LazyFallback />}><ProductionRunsDashboardPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/production/runs/:runId"
            element={
              <RequireRole moduleId="production">
                <Suspense fallback={<LazyFallback />}><ProductionRunFlowPage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/bom"
            element={<Navigate to="/production" replace />}
          />

          <Route
            path="/fadvisor"
            element={
              <RequireRole moduleId="fadvisor">
                <Suspense fallback={<LazyFallback />}><FadvisorPage /></Suspense>
              </RequireRole>
            }
          />

          <Route
            path="/reports"
            element={
              <RequireRole moduleId="reports">
                <Suspense fallback={<LazyFallback />}><ReportsPage /></Suspense>
              </RequireRole>
            }
          />

          <Route
            path="/ai"
            element={
              <RequireRole moduleId="ai">
                <Suspense fallback={<LazyFallback />}><AiAdvisorPage /></Suspense>
              </RequireRole>
            }
          />

          <Route
            path="/directories"
            element={
              <RequireRole moduleId="directories">
                <Suspense fallback={<LazyFallback />}><DirectoriesPage /></Suspense>
              </RequireRole>
            }
          />

          <Route
            path="/data-migration"
            element={
              <RequireRole moduleId="data_migration">
                <Suspense fallback={<LazyFallback />}><DataMigrationPage /></Suspense>
              </RequireRole>
            }
          />

          <Route
            path="/billing"
            element={
              <RequireRole moduleId="billing">
                <Suspense fallback={<LazyFallback />}><ModulePlaceholderPage titleKey="nav.billing" /></Suspense>
              </RequireRole>
            }
          />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  ),
);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
